/**
 * <mb-producto-tarro>: ficha del tarro personalizado (sections/mb-producto-tarro.liquid).
 *
 * Sin JavaScript la ficha sigue funcionando (formulario POST /cart/add con validación nativa). Este componente añade:
 * - Galería: ver assets/mb-galeria.js (<mb-galeria>, compartida con la ficha de granel).
 * - Opciones (Tamaño, Personalización...): la variante sale de la combinación elegida (JSON data-variantes);
 *   actualiza id de variante, precio de cada botón, botón principal, ?variant= y emite `mb:variante` en document.
 * - Personalización: con «Con su nombre» aparecen Nombre (obligatorio), Dedicatoria (opcional), Adornos, la mini vista previa y el vinilo;
 *   con «Sin nombre» esos campos quedan desactivados (no se envían). Con ?nombre= / ?frase= se parte de «Con su nombre».
 * - Ocasión: paso opcional («¿Para qué ocasión es?», bloques `ocasion` de la sección): preselecciona adorno, ofrece ideas de dedicatoria,
 *   cambia la foto de la vista previa y guarda la propiedad «Ocasión»; ?ocasion=<clave> la preselecciona.
 * - Nombre y dedicatoria: se rellenan con ?nombre= (o ?frase=) y ?dedicatoria=, quitan emojis, cuentan caracteres y avisan si falta el nombre.
 * - Crea tu tarro: hasta 3 familias (checkboxes), proporción en texto, propiedad «Familias», bandas de color en la vista previa y «Qué lleva».
 * - Tarjeta con mensaje opcional.
 * - Compra: POST /cart/add.js y apertura del carrito lateral de Horizon con CartLinesUpdateEvent (acción `add`).
 * - Barra fija móvil: aparece cuando el botón principal se queda por encima de la pantalla.
 *
 * Evento publicado (contrato con el bloque de envío): document `mb:variante`,
 * detail = { variante_id, peso_g, precio_cents, cantidad: 1 }, al cargar y en cada cambio de variante.
 * El último detalle queda también en window.mbVariante por si un script llega tarde.
 */

/** Misma lógica que assets/mb-vinilo.js: lo que se ve en la vista previa es lo que se diseña. */
const LARGO_MAX = 18;
const LARGO_DEDICATORIA = 40;
const EMOJI = /[\p{Extended_Pictographic}\p{Emoji_Modifier}\p{Regional_Indicator}\uFE0F\u200D\u20E3]/gu;

/** @param {string} valor */
const quitarEmoji = (valor) => valor.replace(EMOJI, '');

/** @param {string} valor */
const limpiarFrase = (valor) => quitarEmoji(valor).replace(/\s+/g, ' ').trim();

/** Nombre que llega por la URL: ?nombre= (carrito) o ?frase= (hero de la portada y enlaces antiguos). */
const parametroNombre = () => {
  const parametros = new URLSearchParams(window.location.search);
  return parametros.get('nombre') ?? parametros.get('frase') ?? '';
};

const raiz = () => window.Shopify?.routes?.root ?? '/';
const reducirMovimiento = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Ejecuta `fn` cuando ya han corrido todos los módulos de la página (los demás scripts ya escuchan). */
function alListo(fn) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => fn(), { once: true });
  } else {
    setTimeout(fn, 0);
  }
}

/** Error devuelto por la tienda al añadir (agotado, límite de stock...). */
class ErrorTienda extends Error {}

/** @param {any} carrito */
function publicarCarrito(carrito) {
  window.mbCarrito = carrito;
  document.dispatchEvent(new CustomEvent('mb:carrito', { detail: carrito }));
}

class MbProductoTarro extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;
  /** @type {HTMLFormElement | null} */
  #form = null;
  /** @type {HTMLInputElement | null} */
  #campo = null;
  /** @type {HTMLInputElement | null} */
  #dedicatoria = null;
  /** @type {HTMLInputElement | null} */
  #idInput = null;
  /** @type {HTMLElement | null} */
  #boton = null;
  /** @type {HTMLElement | null} */
  #barra = null;
  /** @type {{ id: number, opciones: string[], precio: number, precio_fmt: string, peso: number, disponible: boolean }[]} */
  #datos = [];
  #ocupado = false;
  /** @type {number | undefined} */
  #temporizadorNota;
  /** @type {typeof import('@shopify/events') | null} */
  #eventos = null;

  connectedCallback() {
    this.#form = this.querySelector('[data-formulario]');
    this.#campo = this.querySelector('[data-campo]');
    this.#dedicatoria = this.querySelector('[data-dedicatoria]');
    this.#idInput = this.querySelector('[data-id]');
    this.#boton = this.querySelector('[data-enviar]');
    this.#barra = this.querySelector('[data-barra]');

    this.#controller = new AbortController();

    this.#variantes();
    this.#tematicas();
    this.#crea();
    this.#frase();
    this.#personalizacion();
    this.#ocasion();
    this.#tarjeta();
    this.#compra();
    this.#barraFija();

    import('@shopify/events')
      .then((modulo) => {
        this.#eventos = modulo;
      })
      .catch(() => {
        this.#eventos = null;
      });

    alListo(() => this.#anunciarVariante());
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    clearTimeout(this.#temporizadorNota);
  }

  get #signal() {
    return /** @type {AbortController} */ (this.#controller).signal;
  }

  // --------------------------------------------------------------- Variantes

  /** Variante elegida según las opciones marcadas (las opciones sin botones cuentan como comodín). */
  get #varianteActual() {
    return this.#buscar(this.#seleccion()) ?? this.#datos[0];
  }

  /** @returns {Record<number, string>} valor marcado de cada opción, por posición (1-3) */
  #seleccion() {
    /** @type {Record<number, string>} */
    const seleccion = {};
    for (const radio of this.querySelectorAll('[data-opcion]:checked')) {
      if (radio instanceof HTMLInputElement) seleccion[Number(radio.dataset.opcion)] = radio.value;
    }
    return seleccion;
  }

  /** @param {Record<number, string>} seleccion */
  #buscar(seleccion) {
    return this.#datos.find((v) => Object.entries(seleccion).every(([posicion, valor]) => v.opciones[Number(posicion) - 1] === valor));
  }

  /** «Con su nombre» elegido, o tarro siempre personalizado (hay frase y no hay opción). */
  get #personalizado() {
    const con = this.querySelector('[data-con-nombre]');
    if (con instanceof HTMLInputElement) return con.checked;
    return this.#campo !== null;
  }

  #variantes() {
    try {
      this.#datos = JSON.parse(this.querySelector('script[data-variantes]')?.textContent ?? '[]');
    } catch {
      this.#datos = [];
    }
    for (const radio of this.querySelectorAll('[data-opcion]')) {
      radio.addEventListener('change', () => this.#alCambiarVariante(), { signal: this.#signal });
    }
  }

  #alCambiarVariante() {
    const elegida = this.#varianteActual;
    if (!elegida || !this.#idInput) return;

    this.#idInput.value = String(elegida.id);
    this.#idInput.dataset.peso = String(elegida.peso);
    this.#idInput.dataset.precioCents = String(elegida.precio);
    this.#idInput.dataset.precio = elegida.precio_fmt;

    this.#pintarOpciones();
    this.#pintarCompra();

    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(elegida.id));
    window.history.replaceState(window.history.state, '', url);

    this.#anunciarVariante();
  }

  /** Precio de cada botón (lo que costaría cambiando solo esa opción) y el extra de «Con su nombre». */
  #pintarOpciones() {
    const seleccion = this.#seleccion();
    const radios = [...this.querySelectorAll('[data-opcion]')].filter((r) => r instanceof HTMLInputElement);
    for (const radio of radios) {
      const posicion = Number(radio.dataset.opcion);
      const candidata = this.#buscar({ ...seleccion, [posicion]: radio.value });
      radio.disabled = !radio.checked && !candidata?.disponible;

      const etiqueta = radio.closest('label');
      const precio = etiqueta?.querySelector('[data-opcion-precio]');
      if (precio) precio.textContent = candidata?.disponible ? candidata.precio_fmt : 'Agotado';

      const extra = etiqueta?.querySelector('[data-opcion-extra]');
      if (extra) {
        // El primer valor de la opción es la base (Sin nombre): el resto muestra la diferencia de precio
        const base = radios.find((r) => r.dataset.opcion === radio.dataset.opcion);
        const varianteBase = base && base !== radio ? this.#buscar({ ...seleccion, [posicion]: base.value }) : undefined;
        const diferencia = candidata && varianteBase ? candidata.precio - varianteBase.precio : 0;
        // Mismo formato de dinero que la tienda: se cambia solo la cifra de un precio ya formateado
        const cifra = (diferencia / 100).toFixed(2).replace('.', ',');
        extra.textContent = diferencia > 0 && candidata ? `· +${candidata.precio_fmt.replace(/[\d.,]+/, cifra)}` : '';
      }
    }
  }

  /** Precio, etiqueta y estado del botón principal y de la barra fija. */
  #pintarCompra() {
    const v = this.#varianteActual;
    if (!v) return;
    const etiqueta = this.#personalizado ? 'Añadir mi tarro' : 'Añadir al carrito';

    for (const salida of this.querySelectorAll('[data-precio], [data-precio-barra]')) {
      if (!(salida instanceof HTMLInputElement)) salida.textContent = v.precio_fmt;
    }

    const texto = this.querySelector('[data-enviar-texto]');
    if (texto && !this.#ocupado) {
      texto.textContent = '';
      if (v.disponible) {
        const nombre = document.createElement('span');
        nombre.dataset.etiqueta = '';
        nombre.textContent = etiqueta;
        const precio = document.createElement('span');
        precio.dataset.precioBoton = '';
        precio.textContent = v.precio_fmt;
        texto.append(nombre, ' · ', precio);
      } else {
        texto.textContent = 'Agotado';
      }
    }
    if (this.#boton instanceof HTMLButtonElement) this.#boton.disabled = !v.disponible;
    const botonBarra = this.#barra?.querySelector('[data-barra-enviar]');
    if (botonBarra) botonBarra.textContent = v.disponible ? etiqueta : 'Agotado';
    this.#pintarPromesa();
  }

  /**
   * Línea de promesa: con «Con su nombre» (o tarro siempre personalizado), la de la sección; sin él,
   * «… personalízalo con su nombre por +X €» con la diferencia real de la variante «Con su nombre».
   */
  #pintarPromesa() {
    const promesa = this.querySelector('[data-promesa]');
    const v = this.#varianteActual;
    if (!(promesa instanceof HTMLElement) || !v) return;

    let texto = promesa.dataset.promesaCon ?? '';
    if (!this.#personalizado) {
      texto = promesa.dataset.promesaSin ?? '';
      const con = this.querySelector('[data-con-nombre]');
      if (con instanceof HTMLInputElement) {
        const candidata = this.#buscar({ ...this.#seleccion(), [Number(con.dataset.opcion)]: con.value });
        const diferencia = candidata?.disponible ? candidata.precio - v.precio : 0;
        if (candidata && diferencia > 0) {
          // Mismo formato de dinero que la tienda: se cambia solo la cifra del precio ya formateado
          const cifra = (diferencia / 100).toFixed(2).replace('.', ',');
          texto += ` por +${candidata.precio_fmt.replace(/[\d.,]+/, cifra)}`;
        }
      }
    }
    promesa.textContent = texto;
    promesa.toggleAttribute('hidden', texto === '');
  }

  // ----------------------------------------------------------- Personalización

  /** Frase, mini vista previa y vinilo de la galería solo con «Con su nombre»; sin él, el campo no se envía. */
  #personalizacion() {
    const con = this.querySelector('[data-con-nombre]');
    // ?dedicatoria= (buscador de regalos) también activa «Con su nombre»: sin él, el campo de dedicatoria no se ve
    const inicial = parametroNombre() || new URLSearchParams(window.location.search).get('dedicatoria') || '';
    if (con instanceof HTMLInputElement && inicial && !con.checked && !con.disabled) con.checked = true;

    // ?adornos= (viene de «Cambiar personalización» del carrito): clave (corazones...) o texto (Sin adornos)
    const pedido = new URLSearchParams(window.location.search).get('adornos')?.trim().toLowerCase();
    if (pedido) {
      const radio = this.#adornosRadios.find((r) => r.dataset.adorno === pedido || r.value.toLowerCase() === pedido);
      if (radio) radio.checked = true;
    }
    for (const radio of this.#adornosRadios) {
      radio.addEventListener('change', () => this.#sincronizarAdornos(), { signal: this.#signal });
    }
    window.addEventListener('pageshow', () => this.#sincronizarAdornos(), { signal: this.#signal });

    const aplicar = () => {
      const activo = this.#personalizado;
      if (this.#campo) this.#campo.disabled = !activo;
      if (this.#dedicatoria) this.#dedicatoria.disabled = !activo;
      for (const radio of this.#adornosRadios) radio.disabled = !activo;
      const bloque = this.querySelector('[data-frase-bloque]');
      if (bloque instanceof HTMLElement) bloque.hidden = !activo;
      for (const vinilo of this.querySelectorAll('mb-vinilo')) vinilo.toggleAttribute('hidden', !activo);
      this.#pintarCompra();
    };
    for (const radio of this.querySelectorAll('[data-opcion]')) {
      radio.addEventListener('change', aplicar, { signal: this.#signal });
    }

    // Estado inicial coherente con lo marcado (y con ?frase=)
    if (con instanceof HTMLInputElement) this.#alCambiarVariante();
    aplicar();
    this.#sincronizarAdornos();
  }

  /** @returns {HTMLInputElement[]} */
  get #adornosRadios() {
    return [...this.querySelectorAll('[data-adorno]')].filter((r) => r instanceof HTMLInputElement);
  }

  /** Pasa el conjunto de adornos elegido a todas las vistas previas (galería, mini y las que haya). */
  #sincronizarAdornos() {
    const marcado = this.#adornosRadios.find((r) => r.checked);
    if (!marcado) return;
    for (const vinilo of this.querySelectorAll('mb-vinilo')) vinilo.setAttribute('adornos', marcado.dataset.adorno ?? 'corazones');
  }

  // ------------------------------------------------------------------ Ocasión

  /**
   * Paso «¿Para qué ocasión es?»: adorno preseleccionado, ideas de dedicatoria, foto del tarro de la ocasión,
   * propiedad «Ocasión», ?ocasion= en la URL y evento seleccion_ocasion. Una dedicatoria ya escrita no se pisa al cambiar de ocasión.
   */
  #ocasion() {
    const paso = this.querySelector('[data-ocasion-paso]');
    if (!paso) return;
    /** @type {{ clave: string, nombre: string, adornos: string, ayuda: string, frases: string[], foto: { src: string, srcset: string, mini: string, miniatura: string, proporcion: number } | null }[]} */
    let ocasiones = [];
    try {
      ocasiones = JSON.parse(this.querySelector('script[data-ocasiones]')?.textContent ?? '[]');
    } catch {
      ocasiones = [];
    }
    const radios = [...paso.querySelectorAll('[data-ocasion-radio]')].filter((r) => r instanceof HTMLInputElement);
    const oculto = this.querySelector('[data-ocasion-valor]');
    const ayuda = this.querySelector('[data-ocasion-ayuda]');
    const bloqueIdeas = this.querySelector('[data-ocasion-ideas]');
    const listaIdeas = this.querySelector('[data-ocasion-lista]');
    if (!(oculto instanceof HTMLInputElement) || ocasiones.length === 0) return;

    const parametros = new URLSearchParams(window.location.search);

    /** @param {string} clave */
    const buscar = (clave) => ocasiones.find((o) => o.clave === clave.trim().toLowerCase());

    const pintarIdeas = (/** @type {string[]} */ frases) => {
      if (!(listaIdeas instanceof HTMLElement) || !(bloqueIdeas instanceof HTMLElement)) return;
      listaIdeas.replaceChildren();
      for (const frase of frases) {
        const boton = document.createElement('button');
        boton.type = 'button';
        boton.className = 'mb-ptarro__chip mb-ptarro__chip--idea';
        boton.textContent = frase;
        boton.dataset.frase = frase;
        boton.setAttribute('aria-pressed', String(this.#dedicatoria?.value === frase));
        listaIdeas.append(boton);
      }
      bloqueIdeas.hidden = frases.length === 0;
    };

    // Pulsar una idea: marca «Con su nombre» si hace falta y rellena la dedicatoria (con el mismo evento que si se escribiera)
    listaIdeas?.addEventListener(
      'click',
      (evento) => {
        const boton = evento.target instanceof Element ? evento.target.closest('[data-frase]') : null;
        const campo = this.#dedicatoria;
        if (!(boton instanceof HTMLElement) || !campo) return;
        const con = this.querySelector('[data-con-nombre]');
        if (con instanceof HTMLInputElement && !con.checked && !con.disabled) {
          con.checked = true;
          con.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (campo.disabled) return;
        const largo = campo.maxLength > 0 ? campo.maxLength : LARGO_DEDICATORIA;
        campo.value = limpiarFrase(boton.dataset.frase ?? '').slice(0, largo);
        campo.dispatchEvent(new Event('input', { bubbles: true }));
        this.#anunciar('Dedicatoria añadida');
      },
      { signal: this.#signal }
    );
    // El botón de la idea queda marcado mientras la dedicatoria sea justo esa frase
    this.#dedicatoria?.addEventListener(
      'input',
      () => {
        for (const boton of listaIdeas?.querySelectorAll('[data-frase]') ?? []) {
          if (boton instanceof HTMLElement) boton.setAttribute('aria-pressed', String(boton.dataset.frase === this.#dedicatoria?.value));
        }
      },
      { signal: this.#signal }
    );

    /** @param {string} valor */
    const elegirAdorno = (valor) => {
      const pedido = valor.trim().toLowerCase();
      if (!pedido) return;
      const radio = this.#adornosRadios.find((r) => r.dataset.adorno === pedido || r.value.toLowerCase() === pedido);
      if (!radio || radio.checked) return;
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    };

    // Foto de la ocasión: sustituye la base de la vista previa (galería, mini y miniatura) y se restaura con «Otra ocasión»
    const fotos = [
      { img: this.querySelector('.mb-ptarro__escenario .mb-ptarro__imagen'), escenario: '.mb-ptarro__escenario', clave: 'src', ancho: true },
      { img: this.querySelector('.mb-ptarro__mini-escenario .mb-ptarro__imagen'), escenario: '.mb-ptarro__mini-escenario', clave: 'mini', ancho: false },
      { img: this.querySelector('[data-miniaturas] [data-miniatura] img'), escenario: '', clave: 'miniatura', ancho: false },
    ].flatMap((f) => (f.img instanceof HTMLImageElement ? [{ ...f, img: f.img }] : []));
    const originales = fotos.map((f) => {
      const caja = f.escenario ? this.querySelector(f.escenario) : null;
      return {
        src: f.img.getAttribute('src'),
        srcset: f.img.getAttribute('srcset'),
        proporcion: caja instanceof HTMLElement ? caja.style.getPropertyValue('--mb-ptarro-proporcion') : '',
      };
    });
    const pintarFoto = (/** @type {{ src: string, srcset: string, mini: string, miniatura: string, proporcion: number } | null} */ foto) => {
      fotos.forEach((f, i) => {
        const caja = f.escenario ? this.querySelector(f.escenario) : null;
        const original = originales[i];
        if (foto) {
          f.img.removeAttribute('srcset');
          f.img.src = foto[/** @type {'src' | 'mini' | 'miniatura'} */ (f.clave)];
          if (f.ancho) f.img.setAttribute('srcset', foto.srcset);
          if (caja instanceof HTMLElement) caja.style.setProperty('--mb-ptarro-proporcion', String(foto.proporcion));
        } else {
          if (original.srcset) f.img.setAttribute('srcset', original.srcset);
          else f.img.removeAttribute('srcset');
          if (original.src) f.img.setAttribute('src', original.src);
          if (caja instanceof HTMLElement && original.proporcion) caja.style.setProperty('--mb-ptarro-proporcion', original.proporcion);
        }
      });
    };

    /**
     * @param {typeof ocasiones[number] | undefined} item
     * @param {{ usuario: boolean }} opciones
     */
    const aplicar = (item, { usuario }) => {
      oculto.value = item?.nombre ?? '';
      oculto.disabled = !item;
      if (ayuda) ayuda.textContent = item?.ayuda ?? '';
      pintarIdeas(item?.frases ?? []);
      pintarFoto(item?.foto ?? null);
      // ?adornos= (cambiar personalización desde el carrito) manda sobre el adorno de la ocasión al cargar
      if (item?.adornos && (usuario || !parametros.get('adornos'))) elegirAdorno(item.adornos);
      if (!usuario) return;

      const url = new URL(window.location.href);
      if (item) url.searchParams.set('ocasion', item.clave);
      else url.searchParams.delete('ocasion');
      window.history.replaceState(window.history.state, '', url);

      const clave = item?.clave ?? 'otra';
      if (window.mbDataLayer && typeof window.mbDataLayer.push === 'function') {
        window.mbDataLayer.push('seleccion_ocasion', { ocasion: clave });
      } else {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'seleccion_ocasion', ocasion: clave });
      }
    };

    for (const radio of radios) {
      radio.addEventListener('change', () => radio.checked && aplicar(buscar(radio.value), { usuario: true }), { signal: this.#signal });
    }

    // Estado inicial: ?ocasion= si existe; si no, lo que el navegador haya dejado marcado (volver atrás)
    const pedida = parametros.get('ocasion');
    const inicial = pedida ? buscar(pedida) : undefined;
    if (inicial) {
      const radio = radios.find((r) => r.value === inicial.clave);
      if (radio) radio.checked = true;
    }
    const marcada = radios.find((r) => r.checked);
    aplicar(marcada ? buscar(marcada.value) : undefined, { usuario: false });
  }

  #anunciarVariante() {
    const elegida = this.#varianteActual;
    if (!elegida) return;
    const detalle = {
      variante_id: elegida.id,
      peso_g: Number(elegida.peso) || 0,
      media_id: Number(elegida.media) || 0,
      precio_cents: Number(elegida.precio) || 0,
      cantidad: 1,
    };
    window.mbVariante = detalle;
    document.dispatchEvent(new CustomEvent('mb:variante', { detail: detalle }));
  }

  // --------------------------------------------------------------- Crea tu tarro

  /** Hasta 3 familias: texto de proporción, propiedad «Familias», bandas de la vista previa y «Qué lleva». */
  #crea() {
    const bloque = this.querySelector('[data-crea]');
    if (!bloque) return;
    const MAXIMO = 3;
    const casillas = [...bloque.querySelectorAll('[data-fam]')].filter((c) => c instanceof HTMLInputElement);
    const salida = bloque.querySelector('[data-crea-prop]');
    const valor = bloque.querySelector('[data-crea-valor]');
    const aviso = bloque.querySelector('[data-crea-aviso]');
    const error = bloque.querySelector('[data-crea-error]');
    /** Orden de elección: las bandas suben en ese orden (la primera abajo). */
    let orden = casillas.filter((c) => c.checked);

    /** @param {string[]} n */
    const proporcion = (n) => {
      if (n.length === 1) return `Tarro entero de ${n[0]}`;
      if (n.length === 2) return `Mitad ${n[0]} · mitad ${n[1]}`;
      return n.map((x) => `⅓ ${x}`).join(' · ');
    };

    const pintar = () => {
      const nombres = orden.map((c) => c.dataset.nombre ?? '');
      const texto = orden.length > 0 ? proporcion(nombres) : '';
      if (salida) salida.textContent = texto;
      if (valor instanceof HTMLInputElement) valor.value = texto;
      if (aviso) aviso.textContent = orden.length >= MAXIMO ? 'Máximo 3 familias' : '';
      if (error && orden.length > 0) error.textContent = '';
      for (const c of casillas) c.disabled = !c.checked && orden.length >= MAXIMO;

      // Bandas (cada una cubre desde abajo hasta su tramo; la capa entera va al 55 %)
      for (const capa of this.querySelectorAll('[data-capas]')) {
        const bandas = [...capa.querySelectorAll('.mb-ptarro__banda')].filter((b) => b instanceof HTMLElement);
        bandas.forEach((banda, k) => {
          const color = orden[Math.min(k, orden.length - 1)]?.dataset.color;
          if (color) banda.style.backgroundColor = color;
          banda.style.height = k < orden.length ? `${((k + 1) / orden.length) * 100}%` : '0%';
        });
      }

      // Qué lleva: solo las familias elegidas, en el orden de elección
      const claves = orden.map((c) => c.value);
      for (const fam of this.querySelectorAll('[data-familia]')) {
        if (!(fam instanceof HTMLElement)) continue;
        const posicion = claves.indexOf(fam.dataset.familia ?? '');
        fam.hidden = posicion === -1;
        fam.style.order = String(posicion);
      }
    };

    for (const c of casillas) {
      c.addEventListener(
        'change',
        () => {
          orden = c.checked ? [...orden, c] : orden.filter((x) => x !== c);
          pintar();
        },
        { signal: this.#signal }
      );
    }
    // Al volver con «atrás» el navegador puede restaurar casillas sin lanzar «change»
    window.addEventListener(
      'pageshow',
      () => {
        orden = [...orden.filter((c) => c.checked), ...casillas.filter((c) => c.checked && !orden.includes(c))];
        pintar();
      },
      { signal: this.#signal }
    );
    pintar();
  }

  /** Crea tu tarro exige al menos una familia. @returns {boolean} true si falta y se ha avisado */
  #faltaFamilia() {
    const bloque = this.querySelector('[data-crea]');
    if (!bloque || bloque.querySelector('[data-fam]:checked')) return false;
    const error = bloque.querySelector('[data-crea-error]');
    if (error) error.textContent = 'Elige al menos una familia';
    const primera = bloque.querySelector('[data-fam]');
    if (primera instanceof HTMLElement) {
      primera.scrollIntoView({ block: 'center', behavior: reducirMovimiento() ? 'auto' : 'smooth' });
      primera.focus({ preventScroll: true });
    }
    return true;
  }

  // --------------------------------------------------------------- Temáticas

  #tematicas() {
    const ayuda = this.querySelector('[data-ayuda-tematica]');
    if (!ayuda) return;
    const pintar = () => {
      const marcada = this.querySelector('[data-chips] input:checked');
      ayuda.textContent = marcada instanceof HTMLInputElement ? (marcada.dataset.descripcion ?? '') : '';
    };
    for (const radio of this.querySelectorAll('[data-chips] input')) {
      radio.addEventListener('change', pintar, { signal: this.#signal });
    }
    pintar();
  }

  // ------------------------------------------------------------------- Frase

  #frase() {
    const campo = this.#campo;
    if (!campo) return;
    const contador = this.querySelector('[data-contador-frase]');
    const nota = this.querySelector('[data-nota]');
    const error = this.querySelector('[data-error]');
    const mensajeVacio = this.dataset.msgVacio ?? 'Escribe el nombre o la frase para el tarro';
    const mensajeEmoji = this.dataset.msgEmoji ?? '';
    let componiendo = false;

    const contar = () => {
      if (contador) contador.textContent = `${campo.value.length}/${LARGO_MAX}`;
    };

    /** @param {string} mensaje */
    const avisarNota = (mensaje) => {
      if (!nota) return;
      clearTimeout(this.#temporizadorNota);
      nota.textContent = '';
      requestAnimationFrame(() => {
        nota.textContent = mensaje;
      });
      this.#temporizadorNota = window.setTimeout(() => {
        nota.textContent = '';
      }, 5000);
    };

    const quitarEmojis = () => {
      const limpio = quitarEmoji(campo.value);
      if (limpio === campo.value) return;
      const quitados = campo.value.length - limpio.length;
      const cursor = Math.max(0, (campo.selectionStart ?? limpio.length) - quitados);
      campo.value = limpio;
      campo.setSelectionRange(cursor, cursor);
      avisarNota(mensajeEmoji);
    };

    campo.addEventListener('compositionstart', () => (componiendo = true), { signal: this.#signal });
    campo.addEventListener(
      'compositionend',
      () => {
        componiendo = false;
        quitarEmojis();
        contar();
      },
      { signal: this.#signal }
    );

    campo.addEventListener(
      'input',
      () => {
        if (!componiendo) quitarEmojis();
        campo.setCustomValidity('');
        campo.removeAttribute('aria-invalid');
        if (error) error.textContent = '';
        contar();
      },
      { signal: this.#signal }
    );

    // Mensaje de validación en español, tanto el globo nativo como el aviso visible junto al campo
    campo.addEventListener(
      'invalid',
      () => {
        campo.setCustomValidity(mensajeVacio);
        campo.setAttribute('aria-invalid', 'true');
        if (error) error.textContent = mensajeVacio;
      },
      { signal: this.#signal }
    );

    // Enter cierra el teclado en vez de enviar el formulario sin revisar las demás opciones
    campo.addEventListener(
      'keydown',
      (evento) => {
        if (evento.key !== 'Enter') return;
        evento.preventDefault();
        campo.blur();
      },
      { signal: this.#signal }
    );

    // Valor inicial: ?nombre= / ?frase= (hero de la portada, carrito)
    const inicial = parametroNombre();
    if (inicial && !campo.value) {
      campo.value = limpiarFrase(inicial).slice(0, LARGO_MAX);
      if (quitarEmoji(inicial) !== inicial) avisarNota(mensajeEmoji);
    }
    // Al volver con "atrás" el navegador puede restaurar el valor sin lanzar "input"
    window.addEventListener('pageshow', () => contar(), { signal: this.#signal });

    contar();
    campo.dispatchEvent(new Event('input', { bubbles: true }));
    this.#dedicatoriaCampo(avisarNota, mensajeEmoji);
  }

  /**
   * Dedicatoria (opcional): quita emojis, cuenta caracteres y se rellena con ?dedicatoria= (carrito).
   * @param {(mensaje: string) => void} avisarNota
   * @param {string} mensajeEmoji
   */
  #dedicatoriaCampo(avisarNota, mensajeEmoji) {
    const campo = this.#dedicatoria;
    if (!campo) return;
    const contador = this.querySelector('[data-contador-dedicatoria]');
    let componiendo = false;

    const contar = () => {
      if (contador) contador.textContent = `${campo.value.length}/${LARGO_DEDICATORIA}`;
    };
    const quitarEmojis = () => {
      const limpio = quitarEmoji(campo.value);
      if (limpio === campo.value) return;
      const cursor = Math.max(0, (campo.selectionStart ?? limpio.length) - (campo.value.length - limpio.length));
      campo.value = limpio;
      campo.setSelectionRange(cursor, cursor);
      avisarNota(mensajeEmoji);
    };

    campo.addEventListener('compositionstart', () => (componiendo = true), { signal: this.#signal });
    campo.addEventListener(
      'compositionend',
      () => {
        componiendo = false;
        quitarEmojis();
        contar();
      },
      { signal: this.#signal }
    );
    campo.addEventListener(
      'input',
      () => {
        if (!componiendo) quitarEmojis();
        contar();
      },
      { signal: this.#signal }
    );
    // Enter cierra el teclado en vez de enviar el formulario sin revisar las demás opciones
    campo.addEventListener(
      'keydown',
      (evento) => {
        if (evento.key !== 'Enter') return;
        evento.preventDefault();
        campo.blur();
      },
      { signal: this.#signal }
    );
    window.addEventListener('pageshow', () => contar(), { signal: this.#signal });

    const inicial = new URLSearchParams(window.location.search).get('dedicatoria');
    if (inicial && !campo.value) campo.value = limpiarFrase(inicial).slice(0, LARGO_DEDICATORIA);
    contar();
    campo.dispatchEvent(new Event('input', { bubbles: true }));
  }

  // ----------------------------------------------------------------- Tarjeta

  #tarjeta() {
    const casilla = this.querySelector('[data-tarjeta-casilla]');
    const caja = this.querySelector('[data-tarjeta-texto]');
    const area = this.querySelector('[data-tarjeta-area]');
    const contador = this.querySelector('[data-contador-tarjeta]');
    if (!(casilla instanceof HTMLInputElement) || !(caja instanceof HTMLElement) || !(area instanceof HTMLTextAreaElement)) return;

    const aplicar = () => {
      caja.hidden = !casilla.checked;
      area.disabled = !casilla.checked;
      if (casilla.checked) area.focus({ preventScroll: true });
    };
    casilla.addEventListener('change', aplicar, { signal: this.#signal });
    area.addEventListener(
      'input',
      () => {
        if (contador) contador.textContent = `${area.value.length}/150`;
      },
      { signal: this.#signal }
    );
  }

  // ------------------------------------------------------------------ Compra

  /** @param {string} mensaje */
  #anunciar(mensaje) {
    const region = this.querySelector('[data-anuncio]');
    if (!region) return;
    region.textContent = '';
    requestAnimationFrame(() => {
      region.textContent = mensaje;
    });
  }

  /** @param {boolean} ocupado */
  #marcarOcupado(ocupado) {
    this.#ocupado = ocupado;
    const texto = this.querySelector('[data-enviar-texto]');
    if (ocupado) {
      this.#boton?.setAttribute('aria-busy', 'true');
      this.#barra?.querySelector('[data-barra-enviar]')?.setAttribute('aria-busy', 'true');
      if (texto) texto.dataset.original = texto.innerHTML;
      if (texto) texto.textContent = 'Añadiendo…';
    } else {
      this.#boton?.removeAttribute('aria-busy');
      this.#barra?.querySelector('[data-barra-enviar]')?.removeAttribute('aria-busy');
      if (texto?.dataset.original) {
        texto.innerHTML = texto.dataset.original;
        delete texto.dataset.original;
      }
    }
  }

  /**
   * Abre el carrito lateral de Horizon. Su propio ajuste «abrir al añadir» (auto_open_cart_drawer) está apagado en este tema,
   * así que se abre aquí de forma explícita; si el ajuste se activa, el cajón ya estará abierto y no se hace nada.
   */
  async #abrirCarrito() {
    const cajon = document.getElementById('cart-drawer');
    if (!cajon) return;
    await customElements.whenDefined('theme-drawer');
    if (!cajon.isOpen) cajon.open();
  }

  #compra() {
    const form = this.#form;
    if (!form) return;
    form.addEventListener('submit', (evento) => this.#enviar(evento), { signal: this.#signal });
  }

  /** @param {SubmitEvent} evento */
  async #enviar(evento) {
    const form = this.#form;
    if (!form) return;
    evento.preventDefault();
    if (this.#ocupado) return;

    if (this.#faltaFamilia()) return;

    // Una frase de solo espacios no cuenta: se deja vacía y se avisa como si faltara
    if (this.#campo && !this.#campo.disabled && this.#campo.value.trim() === '') {
      this.#campo.value = '';
      this.#campo.reportValidity();
      return;
    }

    this.#marcarOcupado(true);
    const errorEnvio = this.querySelector('[data-error-envio]');
    if (errorEnvio) errorEnvio.textContent = '';

    const datos = new FormData(form);
    // Secciones del carrito que Horizon refresca con la propia respuesta de /cart/add.js
    const secciones = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean);
    if (secciones.length > 0) datos.set('sections', secciones.join(','));

    const { CartLinesUpdateEvent, CartErrorEvent } = this.#eventos ?? {};
    const diferido = CartLinesUpdateEvent?.createPromise?.();
    if (diferido && CartLinesUpdateEvent) {
      // Si nadie atiende la promesa no debe saltar un error no capturado
      diferido.promise.catch(() => {});
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: String(datos.get('id')), quantity: 1 }],
          promise: diferido.promise,
        })
      );
    }

    try {
      const respuesta = await fetch(window.Theme?.routes?.cart_add_url ?? `${raiz()}cart/add.js`, {
        method: 'POST',
        body: datos,
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
      });
      const cuerpo = await respuesta.json();
      if (!respuesta.ok || cuerpo.status) {
        throw new ErrorTienda(cuerpo.description || cuerpo.message || 'No se pudo añadir');
      }

      const carritoRespuesta = await fetch(`${raiz()}cart.js`, { headers: { Accept: 'application/json' } });
      const carrito = await carritoRespuesta.json();
      publicarCarrito(carrito);

      diferido?.resolve({
        cart: CartLinesUpdateEvent?.createCartFromAjaxResponse?.(carrito),
        detail: {
          items: carrito.items,
          itemCount: 1,
          source: 'mb-producto-tarro',
          sourceId: this.id || 'mb-producto-tarro',
          productId: this.dataset.productId,
          sections: cuerpo.sections,
          didError: false,
        },
      });

      this.#anunciar('Tarro añadido al carrito');
      this.#abrirCarrito();
    } catch (error) {
      if (error instanceof ErrorTienda) {
        // Respuesta de la tienda (agotado, límite...): se muestra y se deja el carrito como estaba
        const mensaje = this.dataset.msgError ?? '';
        if (errorEnvio) errorEnvio.textContent = mensaje;
        this.#anunciar(mensaje);
        diferido?.reject(error);
        if (CartErrorEvent) {
          this.dispatchEvent(new CartErrorEvent({ error: error.message, code: 'INVALID' }));
        }
      } else {
        // Sin red o respuesta rara: se envía el formulario de forma nativa (añade y redirige al carrito)
        diferido?.reject(error);
        this.#marcarOcupado(false);
        HTMLFormElement.prototype.submit.call(form);
        return;
      }
    }

    this.#marcarOcupado(false);
  }

  // ------------------------------------------------------------- Barra fija

  #barraFija() {
    const barra = this.#barra;
    const boton = this.#boton;
    if (!barra || !boton) return;
    const campo = this.#campo;
    let pasado = false;
    let pie = false;

    const actualizar = () => {
      const visible = pasado && !pie;
      barra.toggleAttribute('data-visible', visible);
      barra.toggleAttribute('inert', !visible);
    };

    // La barra aparece cuando el botón principal se queda por encima de la pantalla
    const observador = new IntersectionObserver(
      ([entrada]) => {
        pasado = !entrada.isIntersecting && entrada.boundingClientRect.top < 0;
        actualizar();
      },
      { threshold: 0 }
    );
    observador.observe(boton);
    this.#signal.addEventListener('abort', () => observador.disconnect());

    // Y se esconde cuando el pie de página entra en pantalla
    const pieDePagina = document.querySelector('footer');
    if (pieDePagina) {
      const observadorPie = new IntersectionObserver(
        ([entrada]) => {
          pie = entrada.isIntersecting;
          actualizar();
        },
        { threshold: 0 }
      );
      observadorPie.observe(pieDePagina);
      this.#signal.addEventListener('abort', () => observadorPie.disconnect());
    }

    barra.querySelector('[data-barra-enviar]')?.addEventListener(
      'click',
      () => {
        if (!this.#form) return;
        if (campo && !campo.disabled && campo.value.trim() === '') {
          campo.scrollIntoView({ block: 'center', behavior: reducirMovimiento() ? 'auto' : 'smooth' });
          campo.focus({ preventScroll: true });
          campo.reportValidity();
          return;
        }
        this.#form.requestSubmit();
      },
      { signal: this.#signal }
    );
  }
}

if (!customElements.get('mb-producto-tarro')) {
  customElements.define('mb-producto-tarro', MbProductoTarro);
}
